import type { LucideIcon } from "lucide-react";
import Link from "next/link";
import { cn } from "@/lib/utils";

interface ContactMethodCardProps {
  icon: LucideIcon;
  label: string;
  value: string;
  href?: string;
  className?: string;
  compact?: boolean;
  premium?: boolean;
}

export function ContactMethodCard({
  icon: Icon,
  label,
  value,
  href,
  className,
  compact = false,
  premium = false,
}: ContactMethodCardProps) {
  const content = (
    <>
      <div
        className={cn(
          "flex shrink-0 items-center justify-center rounded-full text-white",
          premium
            ? "contact-icon-circle"
            : "bg-[var(--programs-green)]",
          compact ? "h-9 w-9" : "h-10 w-10"
        )}
      >
        <Icon size={compact ? 16 : 18} aria-hidden />
      </div>
      <div className="min-w-0">
        <p
          className={cn(
            "text-xs font-semibold uppercase tracking-wide",
            premium ? "text-[var(--contact-muted)]" : "text-muted"
          )}
        >
          {label}
        </p>
        <p
          className={cn(
            "truncate font-medium",
            premium ? "text-[var(--contact-text)]" : "text-foreground",
            compact ? "text-xs" : "text-sm"
          )}
        >
          {value}
        </p>
      </div>
    </>
  );

  const cardClass = cn(
    "flex items-center gap-3 border p-3 shadow-sm transition-all duration-300",
    premium
      ? "rounded-2xl border-[var(--contact-border)] bg-[var(--contact-surface)] hover:-translate-y-1 hover:shadow-md"
      : "rounded-xl border-border bg-surface-elevated transition-colors",
    !premium && href && "hover:border-primary/30 hover:shadow-md",
    premium && href && "hover:border-[var(--contact-primary)]/30",
    className
  );

  if (href) {
    return (
      <Link href={href} className={cardClass}>
        {content}
      </Link>
    );
  }

  return <div className={cardClass}>{content}</div>;
}
